/**
 * Auto-init for the legacy search-form pattern shipped by templates like UTest.
 * The web component strips inline <script> tags from manifest HTML for
 * security, so the form's original submit handler is gone by the time it
 * lands in the shadow root.
 *
 * Pattern: a <form> containing <input name="search"> inside the rendered
 * landing. The original inline handler validated the query, built a Connect
 * link of the shape `${vendorHash}?search=<encoded>&discovery=true`, and
 * dispatched it via window.evaro_connect to open the Connect overlay in
 * discovery mode. We reproduce that here against the SDK's own
 * `dispatchConnectOpen` so the env / iframe-loader chain stays consistent.
 *
 * No-op when no search form is present, so safe to call against every shadow
 * root. Idempotent — a second init against the same root will not double-wire
 * any form.
 */

import { dispatchConnectOpen } from './connect-loader'
import { resolveReferral, withReferral } from './referral'

const WIRED_ATTR = 'data-evaro-search-wired'
const MAX_QUERY_LENGTH = 100

export interface SearchInit {
  /** The vendor hash that becomes the prefix of the discovery link. */
  vendorHash: string
  /** Evaro environment, forwarded to dispatchConnectOpen. */
  env?: string
  /** The <evaro-landing> element — the EventTarget for evaro:open / evaro:error. */
  host: HTMLElement
}

export function initLegacySearch(root: ShadowRoot | Element, init: SearchInit): void {
  if (!init.vendorHash) return
  const forms = Array.from(root.querySelectorAll('form')) as HTMLFormElement[]
  for (const form of forms) {
    const input = form.querySelector('input[name="search"]') as HTMLInputElement | null
    if (!input) continue
    if (form.getAttribute(WIRED_ATTR) === '1') continue
    form.setAttribute(WIRED_ATTR, '1')
    form.addEventListener('submit', (event: Event) => {
      event.preventDefault()
      const query = input.value.trim()
      if (!query || query.length > MAX_QUERY_LENGTH) return
      let link = `${init.vendorHash}?search=${encodeURIComponent(query)}&discovery=true`
      const referral = resolveReferral(form, window.location.search)
      if (referral) link = withReferral(link, referral)
      dispatchConnectOpen(init.host, link, init.env)
      input.value = ''
      input.blur()
    })
  }
}
